/**
 * Hero illustrations for the educational storefront — abstract anatomical
 * line art rendered as SVG. Each illustration is designed to layer over a
 * category gradient: white strokes at low opacity, single thin line weight.
 *
 * Use HeroIllustration with slug='landing' for the landing page generic, or a
 * category slug for the per-category hero. Falls back to the apothecary mark.
 */

interface HeroIllustrationProps {
  slug?: string;
  className?: string;
}

const STROKE = 'rgba(255,255,255,0.45)';
const STROKE_BOLD = 'rgba(255,255,255,0.65)';

export function HeroIllustration({ slug, className }: HeroIllustrationProps) {
  const Illust = ILLUSTRATIONS[slug || ''] || ILLUSTRATIONS.landing;
  return (
    <div className={`absolute inset-0 flex items-center justify-center ${className || ''}`}>
      <Illust />
    </div>
  );
}

const Common = ({ children }: { children: React.ReactNode }) => (
  <svg viewBox="0 0 200 250" className="w-3/4 h-3/4 max-w-[280px]" fill="none" xmlns="http://www.w3.org/2000/svg">
    {children}
  </svg>
);

const ILLUSTRATIONS: Record<string, React.FC> = {
  // Generic apothecary mark for the landing page
  landing: () => (
    <Common>
      {/* mortar */}
      <path d="M 50 140 Q 50 200, 100 200 Q 150 200, 150 140 Z" stroke={STROKE_BOLD} strokeWidth="1.25" />
      {/* pestle */}
      <path d="M 130 60 L 100 130" stroke={STROKE_BOLD} strokeWidth="1.25" strokeLinecap="round" />
      <circle cx="135" cy="55" r="10" stroke={STROKE_BOLD} strokeWidth="1.25" />
      {/* ring lines */}
      <ellipse cx="100" cy="142" rx="50" ry="5" stroke={STROKE} strokeWidth="1" />
      <line x1="65" y1="170" x2="135" y2="170" stroke={STROKE} strokeWidth="1" />
    </Common>
  ),

  'weight-loss': () => (
    <Common>
      {/* torso outline */}
      <path
        d="M 100 30 C 90 30, 82 38, 82 50 L 82 70 C 70 76, 60 90, 60 110 L 60 170 C 60 200, 75 230, 100 230 C 125 230, 140 200, 140 170 L 140 110 C 140 90, 130 76, 118 70 L 118 50 C 118 38, 110 30, 100 30 Z"
        stroke={STROKE_BOLD} strokeWidth="1.25"
      />
      {/* gut glow rings */}
      <circle cx="100" cy="135" r="22" stroke={STROKE} strokeWidth="1" strokeDasharray="2 3" />
      <circle cx="100" cy="135" r="32" stroke={STROKE} strokeWidth="0.8" strokeDasharray="2 4" opacity="0.6" />
      <circle cx="100" cy="135" r="12" stroke={STROKE_BOLD} strokeWidth="1" />
    </Common>
  ),

  'oral-ed': () => (
    <Common>
      {/* tablet outline */}
      <ellipse cx="100" cy="125" rx="50" ry="22" stroke={STROKE_BOLD} strokeWidth="1.25" />
      <line x1="50" y1="125" x2="150" y2="125" stroke={STROKE_BOLD} strokeWidth="1" />
      {/* upward arrows from tablet */}
      <path d="M 75 95 L 75 70 M 70 80 L 75 70 L 80 80" stroke={STROKE} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M 100 90 L 100 55 M 95 65 L 100 55 L 105 65" stroke={STROKE_BOLD} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M 125 95 L 125 70 M 120 80 L 125 70 L 130 80" stroke={STROKE} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
      {/* timing arc below */}
      <path d="M 60 165 Q 100 195, 140 165" stroke={STROKE} strokeWidth="1" strokeDasharray="3 3" />
    </Common>
  ),

  'sexual-health': () => (
    <Common>
      {/* two interlocking circles (Venn-style intimacy) */}
      <circle cx="80" cy="125" r="55" stroke={STROKE_BOLD} strokeWidth="1.25" />
      <circle cx="120" cy="125" r="55" stroke={STROKE_BOLD} strokeWidth="1.25" />
      {/* heart in the overlap */}
      <path
        d="M 100 130 C 100 122, 92 115, 88 122 C 85 128, 100 145, 100 145 C 100 145, 115 128, 112 122 C 108 115, 100 122, 100 130 Z"
        stroke={STROKE_BOLD} strokeWidth="1.25" fill={STROKE}
      />
    </Common>
  ),

  dermatology: () => (
    <Common>
      {/* face profile */}
      <path
        d="M 60 50 Q 60 30, 90 30 L 110 30 Q 145 35, 150 80 L 145 130 Q 140 145, 130 150 L 130 175 Q 130 200, 105 200 L 90 200 Q 70 200, 70 180 L 70 160 Q 50 160, 50 140 Q 50 120, 60 110 L 55 90 Q 55 70, 60 50 Z"
        stroke={STROKE_BOLD} strokeWidth="1.25"
      />
      {/* eye */}
      <ellipse cx="118" cy="100" rx="6" ry="3" stroke={STROKE_BOLD} strokeWidth="1" />
      {/* skin layer hatch lines */}
      <path d="M 158 70 L 175 65" stroke={STROKE} strokeWidth="0.8" />
      <path d="M 156 90 L 178 87" stroke={STROKE} strokeWidth="0.8" />
      <path d="M 154 110 L 178 110" stroke={STROKE} strokeWidth="0.8" />
      <path d="M 152 130 L 175 133" stroke={STROKE} strokeWidth="0.8" />
      <path d="M 148 150 L 168 155" stroke={STROKE} strokeWidth="0.8" />
    </Common>
  ),

  peptides: () => (
    <Common>
      {/* peptide chain — linked nodes in a curved arrangement */}
      <path d="M 30 180 Q 60 100, 100 120 T 170 60" stroke={STROKE} strokeWidth="1" fill="none" />
      <circle cx="30" cy="180" r="8" stroke={STROKE_BOLD} strokeWidth="1.25" fill={STROKE} />
      <circle cx="60" cy="135" r="8" stroke={STROKE_BOLD} strokeWidth="1.25" />
      <circle cx="90" cy="118" r="8" stroke={STROKE_BOLD} strokeWidth="1.25" fill={STROKE} />
      <circle cx="125" cy="105" r="8" stroke={STROKE_BOLD} strokeWidth="1.25" />
      <circle cx="160" cy="80" r="8" stroke={STROKE_BOLD} strokeWidth="1.25" fill={STROKE} />
      {/* secondary chain */}
      <path d="M 50 220 Q 90 180, 130 200" stroke={STROKE} strokeWidth="0.8" strokeDasharray="2 3" />
    </Common>
  ),

  'peptides-healing': () => (
    <Common>
      {/* concentric circles converging on a target — repair zone */}
      <circle cx="100" cy="125" r="50" stroke={STROKE} strokeWidth="0.8" strokeDasharray="3 3" />
      <circle cx="100" cy="125" r="35" stroke={STROKE} strokeWidth="1" />
      <circle cx="100" cy="125" r="20" stroke={STROKE_BOLD} strokeWidth="1.25" />
      {/* repair mark */}
      <path d="M 92 125 L 100 132 L 110 118" stroke={STROKE_BOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* surrounding pulse rings */}
      <circle cx="100" cy="125" r="65" stroke={STROKE} strokeWidth="0.6" strokeDasharray="1 4" opacity="0.7" />
      <circle cx="100" cy="125" r="80" stroke={STROKE} strokeWidth="0.5" strokeDasharray="1 5" opacity="0.5" />
    </Common>
  ),

  'peptides-growth-hormone': () => (
    <Common>
      {/* head profile with pituitary highlight */}
      <path
        d="M 100 30 Q 60 30, 50 80 Q 45 110, 60 140 L 65 170 Q 70 195, 95 200 L 110 200 L 110 175 L 130 175 L 130 145 Q 150 130, 150 95 Q 150 35, 100 30 Z"
        stroke={STROKE_BOLD} strokeWidth="1.25"
      />
      {/* pituitary node */}
      <circle cx="100" cy="115" r="6" fill={STROKE} stroke={STROKE_BOLD} strokeWidth="1" />
      {/* radiating signals */}
      <path d="M 100 115 L 100 95 M 100 115 L 80 110 M 100 115 L 120 110 M 100 115 L 90 130 M 100 115 L 110 130"
        stroke={STROKE_BOLD} strokeWidth="1" strokeLinecap="round" />
    </Common>
  ),

  'peptides-metabolism': () => (
    <Common>
      {/* mitochondrion shape */}
      <path
        d="M 40 125 Q 40 90, 100 80 Q 160 90, 160 125 Q 160 160, 100 170 Q 40 160, 40 125 Z"
        stroke={STROKE_BOLD} strokeWidth="1.25"
      />
      {/* internal cristae lines */}
      <path d="M 60 105 Q 80 115, 60 130" stroke={STROKE} strokeWidth="1" />
      <path d="M 80 100 Q 100 115, 80 140" stroke={STROKE} strokeWidth="1" />
      <path d="M 100 100 Q 120 115, 100 145" stroke={STROKE} strokeWidth="1" />
      <path d="M 120 100 Q 140 115, 120 140" stroke={STROKE} strokeWidth="1" />
      <path d="M 140 105 Q 155 115, 140 130" stroke={STROKE} strokeWidth="1" />
      {/* energy bolt */}
      <path d="M 95 195 L 105 195 L 100 215 L 110 215 L 95 240 L 100 220 L 90 220 L 95 195 Z"
        stroke={STROKE_BOLD} strokeWidth="1" fill={STROKE} />
    </Common>
  ),

  'peptides-collagen': () => (
    <Common>
      {/* triple helix — three intertwining waves */}
      <path d="M 60 40 Q 100 80, 60 120 Q 100 160, 60 200 Q 100 230, 60 250"
        stroke={STROKE_BOLD} strokeWidth="1.25" fill="none" />
      <path d="M 100 40 Q 140 80, 100 120 Q 140 160, 100 200 Q 140 230, 100 250"
        stroke={STROKE} strokeWidth="1.25" fill="none" />
      <path d="M 140 40 Q 100 80, 140 120 Q 100 160, 140 200 Q 100 230, 140 250"
        stroke={STROKE_BOLD} strokeWidth="1.25" fill="none" />
      {/* cross-links */}
      <line x1="60" y1="80" x2="140" y2="80" stroke={STROKE} strokeWidth="0.8" strokeDasharray="2 3" />
      <line x1="60" y1="160" x2="140" y2="160" stroke={STROKE} strokeWidth="0.8" strokeDasharray="2 3" />
      <line x1="60" y1="240" x2="140" y2="240" stroke={STROKE} strokeWidth="0.8" strokeDasharray="2 3" />
    </Common>
  ),

  'daily-vitamins': () => (
    <Common>
      {/* tongue silhouette with droplet */}
      <path d="M 60 200 Q 60 130, 100 130 Q 140 130, 140 200 Q 140 240, 100 250 Q 60 240, 60 200 Z"
        stroke={STROKE_BOLD} strokeWidth="1.25" />
      <path d="M 100 130 Q 100 200, 100 240" stroke={STROKE} strokeWidth="0.8" />
      {/* droplet above */}
      <path d="M 100 80 Q 88 95, 88 105 Q 88 115, 100 115 Q 112 115, 112 105 Q 112 95, 100 80 Z"
        stroke={STROKE_BOLD} strokeWidth="1.25" fill={STROKE} />
      {/* fall arc */}
      <path d="M 100 115 L 100 130" stroke={STROKE_BOLD} strokeWidth="1" strokeDasharray="2 2" />
    </Common>
  ),
};
